import { z } from 'zod';
import { nonEmptyString, utcIsoTimestamp, utcNaiveTimestamp } from './common.ts';
import { service } from './services.ts';

export const incidentSeverityValues = ['low', 'medium', 'high', 'critical'] as const;
export const incidentSeverity = z.enum(incidentSeverityValues);
export type IncidentSeverity = z.infer<typeof incidentSeverity>;

export const incidentStatusValues = ['open', 'investigating', 'resolved'] as const;
export const incidentStatus = z.enum(incidentStatusValues);
export type IncidentStatus = z.infer<typeof incidentStatus>;

export const incident = z.object({
  id: z.number().int().positive(),
  service_id: z.number().int().positive(),
  title: nonEmptyString,
  description: z.string().nullable(),
  severity: z.string(),
  status: z.string(),
  started_at: utcNaiveTimestamp.nullable(),
  resolved_at: utcNaiveTimestamp.nullable(),
  created_at: utcNaiveTimestamp,
  updated_at: utcNaiveTimestamp,
});
export type Incident = z.infer<typeof incident>;

// Response shape on /api/services/:name — includes *_norm_utc derived fields.
export const incidentNormalized = incident.extend({
  started_at_norm_utc: utcIsoTimestamp.nullable(),
  resolved_at_norm_utc: utcIsoTimestamp.nullable(),
});
export type IncidentNormalized = z.infer<typeof incidentNormalized>;

export const incidentHistory = incident.extend({
  archived_at: utcNaiveTimestamp,
});
export const incidentHistoryNormalized = incidentNormalized.extend({
  archived_at: utcNaiveTimestamp,
});
export type IncidentHistoryNormalized = z.infer<typeof incidentHistoryNormalized>;

// POST /api/incidents — body. started_at is intentionally ignored (DB forces UTC now).
export const createIncidentInput = z.object({
  service_id: z.number().int().positive(),
  title: nonEmptyString,
  description: z.string().nullish(),
  severity: z.string().optional(),
  status: z.string().optional(),
});
export type CreateIncidentInput = z.infer<typeof createIncidentInput>;

// PATCH /api/incidents/:id/resolve — body. resolved_at optional (defaults to now UTC).
export const resolveIncidentInput = z.object({
  resolved_at: z.string().optional(),
});
export type ResolveIncidentInput = z.infer<typeof resolveIncidentInput>;

// GET /api/services/:name response.
export const serviceDetailsResponse = z.object({
  service,
  incidents: z.array(incidentNormalized),
  history: z.array(incidentHistoryNormalized),
  tz: z.string(),
});
export type ServiceDetailsResponse = z.infer<typeof serviceDetailsResponse>;

export const summaryResponse = z.object({
  allOk: z.boolean(),
  date: z.string(),
});
export type SummaryResponse = z.infer<typeof summaryResponse>;
